import { useEffect, useState } from 'react';
import {
  Actions,
  AppArea,
  MostRequestHistoryListDocument,
  MostRequestHistoryListFilterInput,
  Subjects,
} from 'src/graphql/generated/graphql';
import { useTable } from 'src/hooks/use-table';
import { MostHistoryTable } from './table';
import { SuggestedTasksField } from './columns/suggested-tasks';
import { RequestHistoryModal } from 'src/components/most/list/history/item/modal';
import { PartialMostRequestHistoryType } from 'src/components/most/list/history/types';
import { Can } from 'src/utils/can';
import { useMostHistory } from 'src/hooks/query/most/use-most-history';

const PAGINATION_LIMIT = 20;

type MostHistoryListProps = {
  filter: MostRequestHistoryListFilterInput;
};

export const MostHistoryList = ({ filter }: MostHistoryListProps) => {
  const [totalCount, setTotalCount] = useState(0);
  const [selectedHistoryItem, setSelectedHistoryItem] =
    useState<PartialMostRequestHistoryType>();
  const { page, setPage } = useTable();
  useEffect(() => {
    setPage(0);
  }, [setPage, filter]);

  const { loading, records } = useMostHistory({
    filter,
    pagination: {
      limit: PAGINATION_LIMIT,
      page,
    },
    onCompleted: (data) =>
      setTotalCount(data?.mostRequestHistoryList?.pagination?.totalCount),
  });

  const recordsWithExpandableContent = records.map((record) => ({
    ...record,
    expandableContent: record.suggestedTasks.length && (
      <SuggestedTasksField
        store={record.store as { id: string; nationalStoreNumber: string }}
        tasks={record.suggestedTasks}
        refetchQueries={[MostRequestHistoryListDocument]}
      />
    ),
  })) as PartialMostRequestHistoryType[];

  const onTableRowClick = (_: any, { rowIndex }: { rowIndex: number }) => {
    setSelectedHistoryItem(records[rowIndex] as PartialMostRequestHistoryType);
  };

  return (
    <Can I={Actions.Read} a={Subjects.History} appArea={AppArea.Most}>
      <MostHistoryTable
        data={recordsWithExpandableContent}
        loading={loading}
        refetchQueries={[MostRequestHistoryListDocument]}
        options={{
          page,
          count: totalCount,
          rowsPerPage: PAGINATION_LIMIT,
          onChangePage: setPage,
          enableNestedDataAccess: '.',
          expandableRows: true,
          onRowClick: onTableRowClick,
        }}
      />
      {selectedHistoryItem && (
        <RequestHistoryModal
          item={selectedHistoryItem}
          onClose={() => setSelectedHistoryItem(undefined)}
        />
      )}
    </Can>
  );
};
